import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import type { GenerateReplyResponse } from '../../../api/submissions/submissions.api';
import {
  downloadQuoteProposal,
  downloadQuoteDocuments,
} from '../../../inbox/QuotesService';
import { downloadSpecimenDoc } from '../../../api/quotes.api';

/**
 * Extracts bucket name from S3 URL
 * Example: "https://cowdocs.s3.us-west-2.amazonaws.com/..." -> "cowdocs"
 */
function extractBucketNameFromUrl(s3Url: string): string {
  try {
    const url = new URL(s3Url);
    const hostname = url.hostname;

    // Extract bucket name from hostname like "cowdocs.s3.us-west-2.amazonaws.com"
    const bucketMatch = hostname.match(/^([^.]+)\.s3\./);
    if (bucketMatch && bucketMatch[1]) {
      return bucketMatch[1];
    }

    throw new Error(`Unable to extract bucket name from URL: ${s3Url}`);
  } catch (error) {
    throw new Error(`Invalid S3 URL format: ${s3Url}`);
  }
}

/**
 * Extracts quotes with READY, BOUND, or INDICATION status from coverageOutcomes
 */
export function extractReadyQuotes(
  coverageOutcomes: GenerateReplyResponse['coverageOutcomes']
): Array<GenerateReplyResponse['coverageOutcomes'][0]> {
  const readyQuotes = coverageOutcomes.filter(
    (outcome) =>
      outcome.outcomeType === 'QUOTED' &&
      ['READY', 'BOUND', 'INDICATION'].includes(outcome.quoteDetails?.quoteStatus) &&
      outcome.quoteDetails?.validQuote === true
  );
  return readyQuotes;
}

/**
 * Creates attachment metadata for a quote proposal that follows the same structure
 * as regular attachments, so it works seamlessly with existing AttachmentChip
 */
export async function createQuoteAttachment(
  quoteOutcome: GenerateReplyResponse['coverageOutcomes'][0],
  submissionSubmittedOn: string
): Promise<MessageInfoAttachment> {
  const { quoteDetails } = quoteOutcome;

  if (!quoteDetails) {
    throw new Error('Quote details are required to create attachment metadata');
  }

  // Generate a unique docId for the quote attachment
  const docId = `quote-${quoteDetails.quoteId}`;

  // Create filename based on quote number
  const docName = `${quoteDetails.quoteNumber}-Proposal.pdf`;

  try {
    // Convert submission submittedOn date to Unix timestamp for the API
    const createdTimestamp = Math.floor(
      new Date(submissionSubmittedOn).getTime() / 1000
    );

    // Fetch the S3 URL and bucket name for the proposal
    const response = await downloadQuoteProposal(
      quoteDetails.accountId,
      quoteDetails.quoteId,
      createdTimestamp
    );

    // The API returns a signed S3 URL string
    const s3Url = response.data as string;

    if (!s3Url || typeof s3Url !== 'string') {
      throw new Error('S3 URL is required but not provided by the API');
    }

    // Extract bucket name from the S3 URL
    const bucketName = extractBucketNameFromUrl(s3Url);
    const s3Path = s3Url;

    return {
      docId,
      docName,
      docContentType: 'application/pdf',
      docSize: '0', // Will be determined when downloaded
      bucketName,
      docTime: new Date().toISOString(),
      docType: 'quote-proposal',
      s3Path, // This allows double-click to open in new tab
      signedS3Url: s3Url, // Use the signed URL from the API response
    };
  } catch (error) {
    console.error('Failed to fetch quote proposal URL:', error);
    // Re-throw the error since we can't create a valid attachment without bucket name
    throw new Error(
      `Failed to create quote attachment: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Creates attachment metadata for a quote application that follows the same structure
 * as regular attachments, so it works seamlessly with existing AttachmentChip
 * Note: Application documents are the same for all quotes, so we use submission number for naming
 */
export async function createQuoteApplicationAttachment(
  quoteOutcome: GenerateReplyResponse['coverageOutcomes'][0],
  submissionSubmittedOn: string,
  submissionNumber: string
): Promise<MessageInfoAttachment> {
  const { quoteDetails } = quoteOutcome;

  if (!quoteDetails) {
    throw new Error('Quote details are required to create attachment metadata');
  }

  // Generate a unique docId for the quote application attachment
  const docId = `quote-application-${quoteDetails.quoteId}`;

  // Create filename based on submission number (Application is same for all quotes)
  const docName = `${submissionNumber}-Application.pdf`;

  try {
    // Convert submission submittedOn date to Unix timestamp for the API
    const createdTimestamp = Math.floor(
      new Date(submissionSubmittedOn).getTime() / 1000
    );

    // Fetch the S3 URL for the quote application
    const response = await downloadQuoteDocuments(
      quoteDetails.accountId,
      quoteDetails.quoteId,
      createdTimestamp,
      false, // isEndorsement - not available in quoteDetails, defaulting to false
      quoteDetails.productType,
      quoteDetails.quoteNumber
    );

    // The API returns a signed S3 URL string
    const s3Url = response.data as string;

    if (!s3Url || typeof s3Url !== 'string') {
      throw new Error('S3 URL is required but not provided by the API');
    }

    // Extract bucket name from the S3 URL
    const bucketName = extractBucketNameFromUrl(s3Url);
    const s3Path = s3Url;

    return {
      docId,
      docName,
      docContentType: 'application/pdf',
      docSize: '0', // Will be determined when downloaded
      bucketName,
      docTime: new Date().toISOString(),
      docType: 'quote-application',
      s3Path, // This allows double-click to open in new tab
      signedS3Url: s3Url, // Use the signed URL from the API response
    };
  } catch (error) {
    console.error('Failed to fetch quote application URL:', error);
    // Re-throw the error since we can't create a valid attachment without bucket name
    throw new Error(
      `Failed to create quote application attachment: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Creates attachment metadata for a specimen document that follows the same structure
 * as regular attachments, so it works seamlessly with existing AttachmentChip
 */
export async function createSpecimenDocumentAttachment(
  quoteOutcome: GenerateReplyResponse['coverageOutcomes'][0],
  submissionSubmittedOn: string
): Promise<MessageInfoAttachment> {
  const { quoteDetails } = quoteOutcome;

  if (!quoteDetails) {
    throw new Error('Quote details are required to create attachment metadata');
  }

  // Generate a unique docId for the specimen document attachment
  const docId = `specimen-doc-${quoteDetails.quoteId}`;

  // Create filename based on quote number
  const docName = `${quoteDetails.quoteNumber}-Specimen.pdf`;

  try {
    // Fetch the S3 URL for the specimen document
    const response = await downloadSpecimenDoc({
      params: {
        quoteNumber: quoteDetails.quoteNumber,
        quoteId: quoteDetails.quoteId,
        accountId: quoteDetails.accountId,
        productType: quoteDetails.productType,
      },
    });

    // The API returns a signed S3 URL string
    const s3Url = response.data as string;

    if (!s3Url || typeof s3Url !== 'string') {
      throw new Error('S3 URL is required but not provided by the API');
    }

    // Extract bucket name from the S3 URL
    const bucketName = extractBucketNameFromUrl(s3Url);
    const s3Path = s3Url;

    return {
      docId,
      docName,
      docContentType: 'application/pdf',
      docSize: '0', // Will be determined when downloaded
      bucketName,
      docTime: new Date().toISOString(),
      docType: 'specimen-document',
      s3Path, // This allows double-click to open in new tab
      signedS3Url: s3Url, // Use the signed URL from the API response
    };
  } catch (error) {
    console.error('Failed to fetch specimen document URL:', error);
    // Re-throw the error since we can't create a valid attachment without bucket name
    throw new Error(
      `Failed to create specimen document attachment: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Processes coverageOutcomes and creates attachment metadata for ready quotes
 * Now includes quote proposals, applications, and specimen documents
 * Note: Application documents are the same for all quotes, so we only upload one
 */
export async function processQuoteAttachments(
  generateReplyResponse: GenerateReplyResponse
): Promise<MessageInfoAttachment[]> {
  const readyQuotes = extractReadyQuotes(
    generateReplyResponse.coverageOutcomes
  );

  if (readyQuotes.length === 0) {
    return [];
  }

  const submissionNumber =
    generateReplyResponse.originalSubmissionSummary.submissionNumber;
  const submittedOn =
    generateReplyResponse.originalSubmissionSummary.submittedOn;

  // Create attachments for each ready quote
  const quoteAttachmentPromises = readyQuotes.flatMap((quoteOutcome) => [
    createQuoteAttachment(quoteOutcome, submittedOn),
    createSpecimenDocumentAttachment(quoteOutcome, submittedOn),
  ]);

  // Create only ONE Application attachment (they are the same for all quotes)
  // Use the first ready quote to get the application document
  const applicationAttachmentPromise = createQuoteApplicationAttachment(
    readyQuotes[0],
    submittedOn,
    submissionNumber
  );

  // Combine all attachment promises
  const allAttachmentPromises = [
    ...quoteAttachmentPromises,
    applicationAttachmentPromise,
  ];

  return Promise.all(allAttachmentPromises);
}
